import { mergeClassName } from '@chain-zen/ui'
import React from 'react'

import { userDisplayName } from '../../utils/user'
import { UserInfoTooltip } from '../shared/user/user-info-tooltip'
import type { IUserInfo, IWorkspace } from './messages/types'
import { VerificationStatus } from './verification-status'

/**
 * A component that displays the name of a user or workspace with a verification icon next to it
 */
export interface UserNameWithVerificationIconProps {
  /**
   * The color of the text
   */
  color?: string
  /**
   * The font size of the button text
   * @default 'md'
   */
  fontSize?: 'sm' | 'md' | 'lg'
  showTooltip?: boolean

  /**
   * The verification status of the user or workspace, will display the verification icon next to the name if true
   * @default true
   */
  showVerificationStatus?: boolean

  user: IUserInfo

  workspace?: IWorkspace
}

const fontSizeClasses = {
  lg: 'text-lg',
  md: 'text-base',
  sm: 'text-sm'
}

/**
 * A component that displays the name of a user or workspace with a verification icon next to its name
 */
export const UserNameWithVerificationIcon = (props: UserNameWithVerificationIconProps) => {
  const textColor = 'text-gray-900 dark:text-gray-100'

  const {
    user,
    workspace,
    fontSize = 'md',
    color = textColor,
    showVerificationStatus = true,
    showTooltip = true
  } = props

  const isVerified = user.isVerified

  const displayName = userDisplayName({
    email: user.email,
    name: user.name,
    phone: user.phone
  })

  return (
    <UserInfoTooltip showTooltip={showTooltip} user={user} workspace={workspace}>
      <div className="flex items-center space-x-1">
        <span className={mergeClassName('truncate font-semibold', fontSizeClasses[fontSize], color)}>
          {displayName}
        </span>
        {showVerificationStatus ? <VerificationStatus isVerified={isVerified || false} /> : null}
      </div>
    </UserInfoTooltip>
  )
}
